const React = require('react');
const { createElement: h, useEffect, useRef, useState } = React;
const { getSavedCredentials, saveCredentials, clearSavedCredentials } = require('../lib/apiClient');
const GoogleSigninButton = require('./GoogleSigninButton');
const AuthBrand = require('./AuthBrand');
require('./LoginPage.css');

var PLAN_LABELS = { free: 'Free Trial', pro: 'Pro', max: 'Max', enterprise: 'Enterprise' };

// Hidden until the OAuth client has the app's JavaScript origins registered
// in Google Cloud Console (button 401s without them). Flip to true to show.
var SHOW_GOOGLE_LOGIN = false;

function getSelectedPlan() {
  try {
    var params = new URLSearchParams(window.location.search);
    var p = params.get('plan');
    if (p && PLAN_LABELS[p]) return p;
  } catch (e) {}
  return null;
}

// `?mode=signup` opens the page on the sign-up form (used by the waitlist
// approval email, where the user has no account yet).
function wantsSignup() {
  try {
    return new URLSearchParams(window.location.search).get('mode') === 'signup';
  } catch (e) {}
  return false;
}

function LoginPage(props) {
  // Localized copy (en/da) passed in from App via landingStrings.login.
  var t = (props.strings && props.strings.login) || {};
  var urlPlan = getSelectedPlan();
  var [mode, setMode] = useState(urlPlan || wantsSignup() ? 'signup' : 'signin');
  var [selectedPlan] = useState(urlPlan);
  var [username, setUsername] = useState('');
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [remember, setRemember] = useState(false);
  var [acceptedTerms, setAcceptedTerms] = useState(false);
  var [newsletter, setNewsletter] = useState(false);
  var [error, setError] = useState('');
  var [isSubmitting, setIsSubmitting] = useState(false);
  // Forgot-password inline flow: shows an email field instead of the login form.
  var [showForgot, setShowForgot] = useState(false);
  var [forgotEmail, setForgotEmail] = useState('');
  var [forgotSending, setForgotSending] = useState(false);
  var [forgotSent, setForgotSent] = useState(false);
  var usernameRef = useRef(null);

  function submitForgot(event) {
    event.preventDefault();
    setError('');
    setForgotSending(true);
    Promise.resolve(props.onForgotPassword(forgotEmail))
      .then(function() { setForgotSent(true); })
      .catch(function(err) {
        setError(err && err.message ? err.message : t.authFailed);
      })
      .finally(function() { setForgotSending(false); });
  }

  function openForgot() {
    setShowForgot(true);
    setForgotSent(false);
    setError('');
    // Seed the email field from whatever the user already typed as username.
    if (!forgotEmail && username && username.indexOf('@') !== -1) setForgotEmail(username);
  }

  function closeForgot() {
    setShowForgot(false);
    setError('');
  }

  // Focus the first field on mount AND whenever the mode flips (signin↔signup).
  // autoFocus alone can't do this: React reuses the same input across modes, so
  // it never remounts to re-fire autoFocus.
  useEffect(function() {
    if (usernameRef.current) usernameRef.current.focus();
  }, [mode]);

  useEffect(function() {
    var saved = getSavedCredentials();
    if (!saved || !saved.username || !saved.password) return;
    setUsername(String(saved.username));
    setPassword(String(saved.password));
    setRemember(true);
  }, []);

  function submit(event) {
    event.preventDefault();
    if (mode === 'signup' && !acceptedTerms) {
      setError(t.termsRequired);
      return;
    }
    setError('');
    setIsSubmitting(true);

    var payload = {
      username: String(username || '').trim(),
      password: String(password || '')
    };

    if (mode === 'signup') {
      payload.email = String(email || '').trim();
      payload.newsletter = !!newsletter;
      if (selectedPlan) {
        payload.plan = selectedPlan;
      }
    }

    var action = mode === 'signup' ? props.onSignup : props.onSignin;

    Promise.resolve(action(payload))
      .then(function(user) {
        if (mode === 'signin' && remember) {
          saveCredentials(payload.username, payload.password);
        } else {
          clearSavedCredentials();
        }
        props.onSuccess(user || null);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : t.authFailed);
      })
      .finally(function() {
        setIsSubmitting(false);
      });
  }

  // Google button callback — the ID token goes to /auth/google, which signs
  // in (or creates) the matching account and returns the same user payload
  // as password auth.
  function handleGoogleCredential(credential) {
    setError('');
    setIsSubmitting(true);
    Promise.resolve(props.onGoogleSignin(credential))
      .then(function(user) {
        props.onSuccess(user || null);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : t.authFailed);
      })
      .finally(function() {
        setIsSubmitting(false);
      });
  }

  var usernameLabel = mode === 'signup' ? t.usernameLabelSignup : t.usernameLabelSignin;
  var usernamePlaceholder = mode === 'signup' ? t.usernamePlaceholderSignup : t.usernamePlaceholderSignin;
  var passwordPlaceholder = mode === 'signup' ? t.passwordPlaceholderSignup : t.passwordPlaceholderSignin;
  var title = mode === 'signup' ? t.signupTitle : t.signinTitle;
  var subtitle = mode === 'signup'
    ? (selectedPlan && selectedPlan !== 'free'
      ? String(t.signupSubtitlePlan || '').replace('{plan}', PLAN_LABELS[selectedPlan])
      : t.signupSubtitle)
    : t.signinSubtitle;

  return h('main', { className: 'lp-root' },
    h('div', { className: 'lp-card lp-card--solo' },
      /* Right panel — form */
      h('div', { className: 'lp-panel lp-panel--right' },
        h('div', { className: 'lp-form-wrap' },
          /* Brand */
          h(AuthBrand),
          showForgot
            ? h('div', { className: 'lp-form-intro' },
                h('h1', { className: 'lp-form-title' }, t.forgotTitle),
                h('p', { className: 'lp-form-subtitle' }, t.forgotSubtitle)
              )
            : h('div', { className: 'lp-form-intro' },
                h('h1', { className: 'lp-form-title' }, title),
                h('p', { className: 'lp-form-subtitle' }, subtitle)
              ),

          /* Forgot-password inline view */
          showForgot
            ? (forgotSent
                ? h('div', { className: 'lp-form' },
                    h('p', { className: 'lp-forgot-sent' }, t.forgotSent),
                    h('button', { className: 'lp-switch__link', type: 'button', onClick: closeForgot }, t.forgotBack)
                  )
                : h('form', { className: 'lp-form', onSubmit: submitForgot },
                    h('div', { className: 'lp-field' },
                      h('label', { className: 'lp-label', htmlFor: 'lp-forgot-email' }, t.emailLabel),
                      h('input', {
                        id: 'lp-forgot-email',
                        className: 'lp-input',
                        type: 'email',
                        value: forgotEmail,
                        onChange: function(e) { setForgotEmail(e.target.value); },
                        placeholder: t.forgotEmailPlaceholder,
                        autoComplete: 'email',
                        required: true
                      })
                    ),
                    error ? h('p', { className: 'lp-error' }, error) : null,
                    h('button', { className: 'lp-submit', type: 'submit', disabled: forgotSending },
                      forgotSending ? t.forgotSending : t.forgotSubmit
                    ),
                    h('div', { className: 'lp-forgot-row' },
                      h('button', { className: 'lp-forgot', type: 'button', onClick: closeForgot }, t.forgotBack)
                    )
                  )
              )
            : null,

          /* Form */
          !showForgot ? h('form', { className: 'lp-form', onSubmit: submit },
            h('div', { className: 'lp-field' },
              h('label', { className: 'lp-label', htmlFor: 'lp-username' }, usernameLabel),
              h('input', {
                id: 'lp-username',
                className: 'lp-input',
                ref: usernameRef,
                value: username,
                onChange: function(e) { setUsername(e.target.value); },
                placeholder: usernamePlaceholder,
                autoComplete: 'username',
                required: true
              })
            ),
            mode === 'signup'
              ? h('div', { className: 'lp-field' },
                  h('label', { className: 'lp-label', htmlFor: 'lp-email' }, t.emailLabel),
                  h('input', {
                    id: 'lp-email',
                    className: 'lp-input',
                    type: 'email',
                    value: email,
                    onChange: function(e) { setEmail(e.target.value); },
                    placeholder: t.emailPlaceholder,
                    autoComplete: 'email',
                    required: true
                  })
                )
              : null,
            h('div', { className: 'lp-field' },
              h('label', { className: 'lp-label', htmlFor: 'lp-password' }, t.passwordLabel),
              h('input', {
                id: 'lp-password',
                className: 'lp-input',
                type: 'password',
                value: password,
                onChange: function(e) { setPassword(e.target.value); },
                placeholder: passwordPlaceholder,
                autoComplete: mode === 'signup' ? 'new-password' : 'current-password',
                required: true,
                minLength: 6
              }),
              mode === 'signin'
                ? h('div', { className: 'lp-forgot-row' },
                    h('button', { className: 'lp-forgot', type: 'button', onClick: openForgot }, t.forgot)
                  )
                : null
            ),
            mode === 'signin'
              ? h('label', { className: 'lp-remember' },
                  h('input', {
                    type: 'checkbox',
                    className: 'lp-remember__checkbox',
                    checked: remember,
                    onChange: function(e) { setRemember(e.target.checked); }
                  }),
                  h('span', { className: 'lp-remember__label' }, t.remember)
                )
              : h('label', { className: 'lp-terms' },
                  h('input', {
                    type: 'checkbox',
                    className: 'lp-terms__checkbox',
                    checked: acceptedTerms,
                    onChange: function(e) { setAcceptedTerms(e.target.checked); }
                  }),
                  h('span', { className: 'lp-terms__label' },
                    t.termsBefore,
                    h('a', { href: '/terms.html', target: '_blank', className: 'lp-terms__link' }, t.termsService),
                    t.termsAnd,
                    h('a', { href: '/privacy.html', target: '_blank', className: 'lp-terms__link' }, t.termsPrivacy)
                  )
                ),
            mode === 'signup'
              ? h('label', { className: 'lp-terms' },
                  h('input', {
                    type: 'checkbox',
                    className: 'lp-terms__checkbox',
                    checked: newsletter,
                    onChange: function(e) { setNewsletter(e.target.checked); }
                  }),
                  h('span', { className: 'lp-terms__label' }, t.newsletter)
                )
              : null,
            error ? h('p', { className: 'lp-error' }, error) : null,
            h('button', { className: 'lp-submit', type: 'submit', disabled: isSubmitting },
              isSubmitting ? t.submitting : (mode === 'signup' ? t.submitSignup : t.submitSignin)
            )
          ) : null,

          /* Divider */
          !showForgot ? h('div', { className: 'lp-divider' },
            h('span', { className: 'lp-divider__line' }),
            h('span', { className: 'lp-divider__text' }, t.or),
            h('span', { className: 'lp-divider__line' })
          ) : null,

          /* Google sign-in */
          SHOW_GOOGLE_LOGIN && !showForgot ? h(GoogleSigninButton, {
            onCredential: handleGoogleCredential,
            text: mode === 'signup' ? 'signup_with' : 'signin_with'
          }) : null,

          /* Switch */
          !showForgot ? h('p', { className: 'lp-switch' },
            mode === 'signup' ? t.switchToSigninPrompt : t.switchToSignupPrompt,
            h('button', {
              className: 'lp-switch__link',
              type: 'button',
              onClick: function() { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(''); }
            }, mode === 'signup' ? t.switchToSignin : t.switchToSignup)
          ) : null
        )
      )
    )
  );
}

module.exports = LoginPage;
